import React, { useState, useEffect } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { invitationsApi, invitationAcceptanceApi } from "../../lib/api"
import { useAuth } from "../../contexts/AuthContext"

const AcceptInvitation = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const { user, isAuthenticated, updateUser } = useAuth()

  const [invitation, setInvitation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [accepting, setAccepting] = useState(false)
  const [acceptError, setAcceptError] = useState(null)

  // Form fields for new user registration
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  useEffect(() => {
    fetchInvitation()
  }, [token])

  const fetchInvitation = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await invitationsApi.get(token)
      setInvitation(data.invitation)
      // Pre-fill the email field with the invited parent's email
      if (data.invitation.parent_email) {
        setEmail(data.invitation.parent_email)
      }
    } catch (err) {
      setError(err.message || "Invitation not found")
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = async (e) => {
    e?.preventDefault()
    setAcceptError(null)

    if (!isAuthenticated) {
      if (!email) {
        setAcceptError("Please enter your email address")
        return
      }
      if (!password) {
        setAcceptError("Please enter a password")
        return
      }
      if (password !== passwordConfirmation) {
        setAcceptError("Passwords do not match")
        return
      }
      if (password.length < 12) {
        setAcceptError("Password must be at least 12 characters")
        return
      }
      if (!acceptedTerms) {
        setAcceptError("You must accept the Terms of Service, Acceptable Use Policy, and Privacy Policy")
        return
      }
    }

    try {
      setAccepting(true)
      const response = await invitationAcceptanceApi.accept(token, {
        email,
        password,
        passwordConfirmation,
        name,
        termsAcceptedAt: new Date().toISOString(),
      })

      updateUser(response.user)
      navigate("/parent-dashboard")
    } catch (err) {
      setAcceptError(err.message || "Failed to accept invitation")
    } finally {
      setAccepting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Invalid Invitation</h1>
          <p className="mt-2 text-gray-600">{error}</p>
          <Link
            to="/"
            className="mt-6 inline-block px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go to Home
          </Link>
        </div>
      </div>
    )
  }

  if (invitation.expired || invitation.status !== "pending") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <svg className="mx-auto h-12 w-12 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="mt-4 text-xl font-semibold text-gray-900">
            {invitation.status === "accepted" ? "Already Accepted" : "Invitation Expired"}
          </h1>
          <p className="mt-2 text-gray-600">
            {invitation.status === "accepted"
              ? "This invitation has already been accepted."
              : "This invitation is no longer valid. Please ask for a new invitation."}
          </p>
          <Link
            to="/"
            className="mt-6 inline-block px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go to Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
            {invitation.student_name?.charAt(0)?.toUpperCase()}
          </div>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Parent Invitation</h1>
          <p className="mt-2 text-gray-600">
            <strong>{invitation.student_name}</strong> has invited you to view their assignment dashboard.
          </p>
        </div>

        <div className="bg-indigo-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-indigo-900 mb-2">What you'll be able to see:</h3>
          <ul className="text-sm text-indigo-800 space-y-1">
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Upcoming assignments and due dates
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Overdue and missing work
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Grades and submission status
            </li>
          </ul>
        </div>

        {acceptError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {acceptError}
          </div>
        )}

        {isAuthenticated ? (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              You're signed in as <strong>{user.email}</strong>.
              {user.role === "student" && (
                <span className="block mt-1 text-amber-600">
                  Your account will be converted to a parent account.
                </span>
              )}
            </p>
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 font-medium"
            >
              {accepting ? "Accepting..." : "Accept Invitation"}
            </button>
          </div>
        ) : (
          <form onSubmit={handleAccept}>
            <p className="text-sm text-gray-600 mb-4">
              Create your parent account to accept this invitation.
            </p>

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50"
                  disabled={accepting}
                  readOnly={!!invitation?.parent_email}
                  required
                />
                {invitation?.parent_email && (
                  <p className="mt-1 text-xs text-gray-500">
                    This is the email address the invitation was sent to
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name (optional)
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={accepting}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={accepting}
                  required
                />
              </div>

              <div>
                <label htmlFor="password-confirmation" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  id="password-confirmation"
                  type="password"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  placeholder="Confirm your password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={accepting}
                  required
                />
              </div>

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="terms"
                    name="terms"
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    disabled={accepting}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="terms" className="text-gray-700">
                    I agree to the{" "}
                    <Link to="/legal/terms" className="font-medium text-indigo-600 hover:text-indigo-500" target="_blank">
                      Terms of Service
                    </Link>
                    ,{" "}
                    <Link to="/legal/acceptable-use" className="font-medium text-indigo-600 hover:text-indigo-500" target="_blank">
                      Acceptable Use Policy
                    </Link>
                    , and{" "}
                    <Link to="/legal/privacy" className="font-medium text-indigo-600 hover:text-indigo-500" target="_blank">
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={accepting}
              className="w-full mt-6 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 font-medium"
            >
              {accepting ? "Creating Account..." : "Create Account & Accept"}
            </button>

            <p className="mt-4 text-center text-sm text-gray-600">
              Already have an account?{" "}
              <Link to="/users/sign_in" className="text-indigo-600 hover:text-indigo-800">
                Sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}

export default AcceptInvitation
